import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Layout, Braces, Database } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";

const TONES = {
  ui: {
    border: "border-accent-violet/25 hover:border-accent-violet/55",
    wash: "from-accent-violet/22 via-accent-violet/[0.04] to-transparent",
    glow: "group-hover:shadow-[0_24px_60px_rgba(139,116,245,0.18)]",
    icon: "border-accent-violet/40 bg-gradient-to-br from-accent-violet/25 to-accent-violet/5 text-accent-violet shadow-[0_0_24px_rgba(139,116,245,0.25)]",
    cta: "border-accent-violet/40 bg-accent-violet/12 text-accent-violet group-hover:border-accent-violet/70 group-hover:bg-accent-violet/20",
    index: "text-accent-violet/30",
    bar: "from-accent-violet via-accent-blue to-transparent",
  },
  api: {
    border: "border-accent-blue/25 hover:border-accent-blue/55",
    wash: "from-accent-blue/20 via-accent-blue/[0.04] to-transparent",
    glow: "group-hover:shadow-[0_24px_60px_rgba(109,132,255,0.18)]",
    icon: "border-accent-blue/40 bg-gradient-to-br from-accent-blue/25 to-accent-blue/5 text-accent-blue shadow-[0_0_24px_rgba(109,132,255,0.25)]",
    cta: "border-accent-blue/40 bg-accent-blue/12 text-accent-blue group-hover:border-accent-blue/70 group-hover:bg-accent-blue/20",
    index: "text-accent-blue/30",
    bar: "from-accent-blue via-accent-violet to-transparent",
  },
  db: {
    border: "border-[#c4a35a]/25 hover:border-[#c4a35a]/50",
    wash: "from-[#c4a35a]/16 via-[#c4a35a]/[0.04] to-transparent",
    glow: "group-hover:shadow-[0_24px_60px_rgba(196,163,90,0.14)]",
    icon: "border-[#c4a35a]/40 bg-gradient-to-br from-[#c4a35a]/20 to-[#c4a35a]/5 text-[#d4b56a] shadow-[0_0_24px_rgba(196,163,90,0.2)]",
    cta: "border-[#c4a35a]/40 bg-[#c4a35a]/12 text-[#d4b56a] group-hover:border-[#c4a35a]/65 group-hover:bg-[#c4a35a]/18",
    index: "text-[#c4a35a]/35",
    bar: "from-[#c4a35a] via-accent-violet/60 to-transparent",
  },
};

const CARDS = [
  {
    id: "ui",
    icon: Layout,
    title: "UI Playground",
    label: "Browser lab",
    description:
      "Practice locators, UI controls, waits, and complex user flows in a live browser lab built for SDETs.",
    cta: "Try UI Playground",
    type: "link",
    href: "/playground",
  },
  {
    id: "api",
    icon: Braces,
    title: "API Playground",
    label: "Swagger lab",
    description:
      "Hit REST endpoints, inspect responses, and validate status codes, headers, and schemas in an interactive Swagger lab.",
    cta: "Try API Playground",
    type: "link",
    href: "/playground/swagger",
  },
  {
    id: "db",
    icon: Database,
    title: "DB Playground",
    label: "SQL lab",
    description:
      "Run SQL queries against sample datasets, verify joins and constraints, and sharpen your database testing skills.",
    cta: "Contact me for credentials",
    type: "scroll",
    to: "contact",
  },
];

export default function AutomationPlayground() {
  return (
    <section
      id="playground"
      className="relative py-28 md:py-36 overflow-hidden"
      data-testid="playground-section"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-16 h-72 bg-[radial-gradient(50%_60%_at_50%_0%,rgba(109,132,255,0.12),transparent_70%)]"
      />

      <div className="section-x relative mx-auto max-w-[1400px]">
        <div className="max-w-3xl">
          <p className="section-eyebrow">05 — Automation Playground</p>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
            Hands-on labs.{" "}
            <span className="text-ink/50">Real practice.</span>
          </h2>
          <p className="mt-5 text-ink/60 max-w-xl md:text-[15px] leading-relaxed">
            Explore UI, API, and database playgrounds designed to sharpen the
            skills that matter in modern QA automation.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {CARDS.map((card, i) => (
            <PlaygroundCard key={card.id} card={card} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PlaygroundCard({ card, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const Icon = card.icon;
  const tone = TONES[card.id];

  const cardClass = `group relative flex h-full flex-col overflow-hidden rounded-[1.6rem] border bg-[#101014]/92 p-6 md:p-7 text-left backdrop-blur-md transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 ${tone.border} ${tone.glow}`;

  const body = (
    <>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${tone.wash}`}
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r ${tone.bar}`}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/[0.03] blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-60"
      />

      <div className="relative flex items-start justify-between gap-3">
        <span
          className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl border ${tone.icon}`}
        >
          <Icon size={20} strokeWidth={1.75} />
        </span>
        <span
          className={`font-display text-3xl font-medium leading-none tabular-nums ${tone.index}`}
          aria-hidden="true"
        >
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      <p className="relative mt-5 text-[10px] uppercase tracking-[0.24em] font-mono text-ink/45">
        {card.label}
      </p>
      <h3 className="relative mt-1.5 font-display text-2xl md:text-[1.65rem] tracking-tight leading-tight">
        {card.title}
      </h3>
      <p className="relative mt-3 text-ink/60 text-[14px] leading-relaxed flex-1">
        {card.description}
      </p>

      <span
        className={`relative mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-[border-color,background-color,transform] duration-300 group-hover:gap-2.5 ${tone.cta}`}
      >
        {card.cta}
        <ArrowUpRight
          size={14}
          className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </span>
    </>
  );

  if (card.type === "link") {
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      >
        <a
          href={card.href}
          className={cardClass}
          data-testid={`playground-card-${card.id}`}
        >
          {body}
        </a>
      </motion.div>
    );
  }

  return (
    <motion.button
      ref={ref}
      type="button"
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => scrollToId(card.to)}
      className={`w-full ${cardClass}`}
      data-testid={`playground-card-${card.id}`}
    >
      {body}
    </motion.button>
  );
}
